<script lang="ts">
	import { subscribeToNewsletter } from "@api/subscribeToNewsletter.js";
	import Alert from "@components/Alert.svelte";
	import type { AlertType } from "@components/AlertType.js";
	import HugeHeading from "@components/HugeHeading.svelte";
	import Button from "@components/common/Button.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Form from "@components/form/Form.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import type { ClassValue } from "svelte/elements";
	import { fade } from "svelte/transition";
	import { ButtonVariant } from "@core/schema/ButtonVariant.js";
	import { NewsletterSource } from "@core/schema/NewsletterSource.js";
	import { getGdprPath } from "@core/schema/paths/getGdprPath.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";

	const { class: customClass }: { class?: ClassValue } = $props();
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	let loading = $state(false);
	let feedback = $state<{ type: AlertType; text: string }>();

	$effect(() => {
		if (feedback) {
			setTimeout(() => {
				feedback = undefined;
			}, 5000);
		}
	});

	async function submitNewsletterForm(event: SubmitEvent, form: HTMLFormElement): Promise<void> {
		event.preventDefault();
		const formData = new FormData(form);
		loading = true;

		try {
			if (!formData.has("gdpr")) {
				throw new DOMException("Musíte souhlasit se zásadami ochrany osobních údajů.", "NotAllowedError");
			}
			const now = new Date().toISOString();
			await subscribeToNewsletter(PUBLIC_GRAPHQL_ENDPOINT, {
				email: formData.get("email") as string,
				gdprApprovedAt: now,
				locale: tenant.locale,
				source: NewsletterSource.NewsletterSection,
			});
			feedback = {
				text: "Vaše zpráva byla úspěšně odeslána. Podrobnosti jsme Vám zaslali na Vaši e-mailovou adresu.",
				type: "success",
			};
		} catch (error) {
			feedback = {
				text: "Vaši zprávu se nepodařilo odeslat. Prosíme vyzkoušejte za chvíli odeslání znovu.",
				type: "warning",
			};
			throw error;
		} finally {
			loading = false;
			form.reset();
		}
	}
</script>

<section class={[customClass, "lg:px-16 full-width grid-container py-8 md:py-16 lg:py-20"]}>
	<HugeHeading
		level={2}
		text="stroke[[Zajistěte si balík]] nl[[]] logistických nl[[]] novinek"
		class="text-center full-width px-8 sm:hidden"
	/>
	<HugeHeading
		level={2}
		text="stroke[[Zajistěte si balík]] nl[[]] logistických novinek"
		class="text-center full-width px-8 hidden sm:block"
	/>
	<Form class="contents" onsubmit={submitNewsletterForm}>
		<!-- The backdrop blur causes the inner recaptcha position fixed to float at this form :) -->
		<div
			class="bg-white/50 backdrop-blur-sm sm:backdrop-blur-[.5rem] -mt-0.5 p-4 md:p-8 flex items-center lg:mt-4 flex-col gap-4 md:gap-16 container-8 mx-auto rounded-xl md:rounded-2xl"
		>
			<p class="font-medium text-xs/[1.4] md:text-2xl/[1.4] text-center">
				<Formatted
					text="Odebírejte náš newsletter a získejte náskok před konkurencí díky nejčerstvějším novinkám ze světa skladování a logistiky"
				/>
			</p>
			<div class="flex flex-col gap-4 md:gap-8 max-w-[17rem] md:max-w-[32rem] w-full">
				<LabelledInput required type="email" name="email" value="" placeholder="Zadejte Váš e-mail"
					>E-mail</LabelledInput
				>
				<LabelledInput required type="checkbox" name="gdpr" wrapperClass="mx-auto flex items-center">
					<Formatted
						text="Přihlašuji se k odběru newsletteru a souhlasím se l[{getGdprPath(
							tenant,
						)}][[zpracováním osobních údajů]]"
					/>
				</LabelledInput>
			</div>

			{#if feedback}
				<div out:fade class="-my-2 md:-my-8">
					<Alert type={feedback.type}>
						<Formatted text={feedback.text} />
					</Alert>
				</div>
			{/if}

			<Button
				icon={loading ? "loading" : "arrow"}
				disabled={loading}
				variant={ButtonVariant.Primary}
				class="gap-2 md:gap-4 w-fit"
			>
				<Formatted text={loading ? "Odesílám" : "Odebírat novinky"} />
			</Button>
		</div>
	</Form>
</section>
